/**
 * Manual document ingestion — the browser end of `POST /v1/documents`.
 *
 * This is the flow you drive by hand to watch the pipeline work end to end:
 * drop files, optionally say where they came from, and submit. Each file is one
 * independent submission carrying its own idempotency key, so a failure part-way
 * through a batch neither loses the files that landed nor double-ingests them on
 * retry. Files are sent one at a time on purpose — a browser-side burst is the
 * quickest way to trip the per-user rate limiter, and sequential submission
 * keeps each row's outcome legible as it happens.
 *
 * The source channel is deliberately absent from everything here: the backend
 * derives it from the credential, so these land as `ui` without being asked to.
 */
import { useCallback, useId, useRef, useState } from 'react';

import type { DocumentCaptureBody } from '../../api/resources';
import { Button, Modal, Select, Spinner } from '../../components';

import { useDocumentsApi } from './client';
import {
  ACCEPT_ATTR,
  acceptedCount,
  isBusy,
  isRetryable,
  sizeLabel,
  toQueueItems,
  uploadErrorMessage,
  type QueueItem,
} from './upload';
import { useUploadRegistries } from './useUploadRegistries';

export interface UploadDialogProps {
  open: boolean;
  /** Close the dialog. Reports how many files reached the pipeline this session. */
  onClose: (accepted: number) => void;
}

/** The pipeline status a fresh submission comes back with, in plain words. */
function outcomeLabel(item: QueueItem): string {
  if (item.result === null) return '';
  if (item.result.duplicate === true) return 'Already ingested';
  return 'Queued for OCR';
}

export function UploadDialog({ open, onClose }: UploadDialogProps): React.JSX.Element {
  const api = useDocumentsApi();
  const { locations, scanners } = useUploadRegistries(open);
  const inputRef = useRef<HTMLInputElement>(null);
  const locationId = useId();
  const scannerId = useId();

  const [items, setItems] = useState<QueueItem[]>([]);
  const [location, setLocation] = useState('');
  const [scanner, setScanner] = useState('');
  const [dragging, setDragging] = useState(false);
  const [running, setRunning] = useState(false);

  const busy = running || isBusy(items);
  const pending = items.filter((item) => item.status === 'queued');

  const addFiles = useCallback((files: FileList | null) => {
    if (files === null || files.length === 0) return;
    // Snapshot into plain `File`s now, synchronously: `files` is the input's own
    // live `FileList`, and the caller resets `input.value` right after this call
    // returns to allow re-picking the same file — React doesn't run a functional
    // `setState` updater until after the whole handler has returned, so reading
    // `files` lazily inside one would see it already cleared.
    const picked = toQueueItems([...files]);
    setItems((current) => [...current, ...picked]);
  }, []);

  // A scanner is mapped to at most one location, so picking either field can
  // suggest the other rather than leave a mismatched pair sitting there. Only
  // acts when a mapping actually exists — otherwise the other field is left
  // exactly as the person set it.
  const chooseLocation = useCallback(
    (id: string) => {
      setLocation(id);
      if (id === '') return;
      const mapped = scanners.find((s) => s.location_id === id);
      if (mapped !== undefined) setScanner(mapped.id);
    },
    [scanners],
  );

  const chooseScanner = useCallback(
    (id: string) => {
      setScanner(id);
      if (id === '') return;
      const chosen = scanners.find((s) => s.id === id);
      if (chosen?.location_id != null) setLocation(chosen.location_id);
    },
    [scanners],
  );

  const patch = useCallback((id: string, change: Partial<QueueItem>) => {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...change } : item)));
  }, []);

  const remove = useCallback((id: string) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  // Re-queue a failed file. It keeps its id, so the retry carries the SAME
  // idempotency key — if the first attempt actually landed despite the error the
  // server answers `duplicate: true` rather than ingesting the document twice.
  const retry = useCallback(
    (id: string) => {
      patch(id, { status: 'queued', error: null });
    },
    [patch],
  );

  const submit = useCallback(() => {
    const queued = items.filter((item) => item.status === 'queued');
    if (queued.length === 0 || running) return;

    const capture: DocumentCaptureBody = {};
    if (location !== '') capture.location_id = location;
    if (scanner !== '') capture.scanner_id = scanner;

    setRunning(true);
    void (async () => {
      // Sequential by design (see the module note): one file's outcome is
      // settled before the next leaves, and the rate limiter stays happy.
      for (const item of queued) {
        patch(item.id, { status: 'uploading', error: null, attempted: true });
        try {
          const result = await api.upload(item.file, capture, item.id);
          patch(item.id, { status: 'done', result, error: null });
        } catch (error) {
          patch(item.id, { status: 'rejected', error: uploadErrorMessage(error) });
        }
      }
      setRunning(false);
    })();
  }, [api, items, location, scanner, running, patch]);

  const close = useCallback(() => {
    if (busy) return;
    const accepted = acceptedCount(items);
    setItems([]);
    onClose(accepted);
  }, [busy, items, onClose]);

  return (
    <Modal
      open={open}
      onClose={close}
      title="Upload documents"
      width="xxl"
      dismissible={!busy}
      footer={
        <div className="ds-upload__footer">
          <span className="ds-upload__footnote">
            Submitted documents run the full pipeline — OCR, then classification.
          </span>
          <Button variant="secondary" onClick={close} disabled={busy}>
            {acceptedCount(items) > 0 ? 'Done' : 'Cancel'}
          </Button>
          <Button variant="primary" onClick={submit} loading={running} disabled={pending.length === 0}>
            {pending.length === 0
              ? 'Upload'
              : `Upload ${String(pending.length)} file${pending.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      }
    >
      <div className="ds-upload">
        {/* The drop zone is a real button: click and Enter/Space both open the
            picker, so the drag-and-drop affordance is never the only way in. */}
        <button
          type="button"
          className={dragging ? 'ds-upload__drop is-dragging' : 'ds-upload__drop'}
          onClick={() => inputRef.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => {
            setDragging(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            addFiles(event.dataTransfer.files);
          }}
        >
          <span className="ds-upload__drop-icon" aria-hidden="true">
            ⇪
          </span>
          <span className="ds-upload__drop-title">Drop files here, or click to choose</span>
          <span className="ds-upload__drop-hint">PDF, PNG, JPEG, TIFF or plain text · up to 50 MB each</span>
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          className="ds-upload__input"
          aria-label="Choose documents to upload"
          onChange={(event) => {
            addFiles(event.currentTarget.files);
            // Reset so re-picking the same file fires `change` again.
            event.currentTarget.value = '';
          }}
        />

        <div className="ds-upload__meta">
          <div className="ds-upload__metafield">
            <label className="ds-upload__metalabel" htmlFor={locationId}>
              Location <span className="ds-upload__optional">optional</span>
            </label>
            <Select
              id={locationId}
              value={location}
              disabled={running}
              options={[
                { value: '', label: 'Not specified' },
                ...locations.map((one) => ({ value: one.id, label: one.name })),
              ]}
              onChange={(event) => {
                chooseLocation(event.currentTarget.value);
              }}
            />
          </div>
          <div className="ds-upload__metafield">
            <label className="ds-upload__metalabel" htmlFor={scannerId}>
              Scanner <span className="ds-upload__optional">optional</span>
            </label>
            <Select
              id={scannerId}
              value={scanner}
              disabled={running}
              options={[
                { value: '', label: 'Not specified' },
                ...scanners.map((one) => ({ value: one.id, label: one.device_id })),
              ]}
              onChange={(event) => {
                chooseScanner(event.currentTarget.value);
              }}
            />
          </div>
        </div>

        {items.length > 0 ? (
          <ul className="ds-upload__list" aria-label="Files to upload">
            {items.map((item) => (
              <QueueRow
                key={item.id}
                item={item}
                onRemove={() => {
                  remove(item.id);
                }}
                onRetry={() => {
                  retry(item.id);
                }}
                actionable={!running && item.status !== 'uploading'}
              />
            ))}
          </ul>
        ) : null}
      </div>
    </Modal>
  );
}

function QueueRow({
  item,
  onRemove,
  onRetry,
  actionable,
}: {
  item: QueueItem;
  onRemove: () => void;
  onRetry: () => void;
  /** False while this row's upload is running — it must not be edited mid-flight. */
  actionable: boolean;
}): React.JSX.Element {
  return (
    <li className="ds-upload__row">
      <span className="ds-upload__name" title={item.file.name}>
        {item.file.name}
      </span>
      <span className="ds-upload__size">{sizeLabel(item.file.size)}</span>
      <span className="ds-upload__state">
        {item.status === 'uploading' ? (
          <>
            <Spinner />
            <span>Uploading…</span>
          </>
        ) : null}
        {item.status === 'queued' ? <span className="ds-upload__muted">Ready</span> : null}
        {item.status === 'done' ? <span className="ds-upload__ok">{outcomeLabel(item)}</span> : null}
        {item.status === 'rejected' ? (
          <>
            <span className="ds-upload__bad" title={item.error ?? undefined}>
              {item.error}
            </span>
            {actionable && isRetryable(item) ? (
              <button
                type="button"
                className="ds-upload__retry"
                onClick={onRetry}
                aria-label={`Retry ${item.file.name}`}
              >
                Retry
              </button>
            ) : null}
          </>
        ) : null}
      </span>
      {actionable ? (
        <button type="button" className="ds-upload__remove" onClick={onRemove} aria-label={`Remove ${item.file.name}`}>
          ✕
        </button>
      ) : (
        <span className="ds-upload__remove-slot" aria-hidden="true" />
      )}
    </li>
  );
}
